import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtDateTime } from '../../utils/region';
import {
  coaMasterApi, CoaAccount, CoaFirmCell, CoaFirmField, CoaFirmSummary, CoaKindCounts, CoaView,
} from '../../utils/api/coamaster';

// ── Admin → Chart template: the FIRM LAYER (COA-5 / decision 10) ────────────────────────────────
// The firm's own judgements on the customer's master chart, shown BESIDE the customer's values —
// never instead of them, and never written into them (server/src/db/coaMaster.ts). Three pieces, all
// drawn from what the server sends (server/src/services/coaMasterEffective.ts):
//   · FirmPanel — whether the layer stands (and every reason it does not), the counts without and with
//     it, and the switch between "the customer's file" and "with the firm's judgements";
//   · FirmNote  — in the firm view, one row's judged cells: customer value beside firm value, the
//     reason, who judged, when, who recorded it and from where, and whether it still stands;
//   · FirmDialog — record a judgement on one cell: set / confirm / withdraw, a field from the server's
//     list, a type from THIS template's own words, a reason that is required.
// ⛔ No rule is decided here. Whether a judgement may be recorded is the server's answer (POST
// /:id/overrides); the screen only refuses to send one without a reason, and shows the server's
// reasons, verbatim, when it says no. Kinds, counts and the "still stands" flag are the server's too.
// ⛔ The customer's values and the firm's reasons are data: shown as written, never translated.

const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: 16, marginBottom: 14 };
const muted: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 };
const field: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '6px 10px', borderBottom: '1px solid var(--border)' };
const amber = { bg: '#FEF6E7', border: '#F5D9A8', fg: '#92400E' };

/** A control role as words; the stored 'ar' / 'ap' stays visible beside it. */
function RoleValue({ v }: { v: string }) {
  const { t } = useTranslation();
  if (!v) return <span style={{ color: 'var(--text-tertiary)' }}>{t('coam_firm_none')}</span>;
  return <span>{v === 'ar' ? t('coam_firm_role_ar') : v === 'ap' ? t('coam_firm_role_ap') : v} <code style={{ fontSize: 11 }}>{v}</code></span>;
}
/** A cell value as written; '' said in words. */
function CellValue({ field: f, v }: { field: string; v: string }) {
  const { t } = useTranslation();
  if (f === 'control_role') return <RoleValue v={v} />;
  if (v === '') return <span style={{ color: 'var(--text-tertiary)' }}>{t('coam_firm_none')}</span>;
  return <span lang={f === 'name_th' ? 'th' : 'en'}>{v}</span>;
}

const COUNT_ROWS: [keyof CoaKindCounts, string][] = [
  ['header', 'coam_firm_count_header'], ['postable', 'coam_firm_count_postable'], ['reference', 'coam_firm_count_reference'],
  ['system', 'coam_firm_count_system'], ['control', 'coam_firm_count_control'], ['ar', 'coam_firm_count_ar'], ['ap', 'coam_firm_count_ap'],
];

export function FirmPanel({ firm, view, onView }: { firm: CoaFirmSummary; view: CoaView; onView: (v: CoaView) => void }) {
  const { t } = useTranslation();
  const show = (c: CoaKindCounts, k: keyof CoaKindCounts) => {
    const v = c[k];
    return Array.isArray(v) ? (v.length ? v.join(', ') : '—') : String(v);
  };
  const seg = (v: CoaView, label: string) => (
    <button key={v} role="radio" aria-checked={view === v} data-view-btn={v} onClick={() => onView(v)}
      style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
        background: view === v ? 'var(--accent)' : 'transparent', color: view === v ? '#fff' : 'var(--text-primary)' }}>
      {label}
    </button>
  );
  return (
    <section data-section="firm" style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <i className="ti ti-scale" style={{ fontSize: 18, color: 'var(--text-secondary)' }} aria-hidden />
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{t('coam_firm_title')}</div>
        <span data-firm-status={firm.ok ? 'ok' : 'blocked'}
          style={{ fontSize: 11.5, fontWeight: 700, padding: '1px 8px', borderRadius: 10,
            background: firm.ok ? '#ECFDF5' : amber.bg, color: firm.ok ? '#047857' : amber.fg, border: `1px solid ${firm.ok ? '#A7F3D0' : amber.border}` }}>
          {firm.ok ? t('coam_firm_ok', { count: firm.cells.length }) : t('coam_firm_blocked')}
        </span>
        <div style={{ flex: 1 }} />
        <div role="radiogroup" aria-label={t('coam_view')} style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
          {seg('customer', t('coam_view_customer'))}
          {seg('firm', t('coam_view_firm'))}
        </div>
      </div>
      <div style={{ ...muted, marginBottom: 8 }}>{t('coam_firm_lead')}</div>
      {!firm.ok && (
        <div data-firm-reasons style={{ fontSize: 12.5, color: amber.fg, background: amber.bg, border: `1px solid ${amber.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('coam_firm_blocked_lead')}</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{firm.reasons.map((r, i) => <li key={i} data-firm-reason-line lang="en">{r}</li>)}</ul>
        </div>
      )}
      <table data-firm-counts aria-label={t('coam_firm_counts')} style={{ borderCollapse: 'collapse', minWidth: 420 }}>
        <thead><tr>
          <th style={th}>{t('coam_firm_counts')}</th>
          <th style={th}>{t('coam_view_customer')}</th>
          <th style={th}>{t('coam_view_firm')}</th>
        </tr></thead>
        <tbody>
          {COUNT_ROWS.map(([k, label]) => (
            <tr key={k} data-firm-count={k}>
              <td style={td}>{t(label)}</td>
              <td data-customer style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{show(firm.counts.customer, k)}</td>
              <td data-effective style={{ ...td, fontVariantNumeric: 'tabular-nums', fontWeight: show(firm.counts.customer, k) !== show(firm.counts.effective, k) ? 700 : 400 }}>{show(firm.counts.effective, k)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** In the firm view, the judged cells of one row — and the button that opens the dialog for it. */
export function FirmNote({ row, onRule }: { row: CoaAccount; onRule: (r: CoaAccount) => void }) {
  const { t } = useTranslation();
  const cells = Object.values(row.firm?.cells || {}).filter(Boolean) as CoaFirmCell[];
  return (
    <div data-firm-col={row.code} style={{ minWidth: 260 }}>
      {cells.map(c => (
        <div key={c.field} data-firm-cell={`${c.code}:${c.field}`} data-firm-action={c.action} data-stale={c.stale ? '1' : '0'}
          style={{ padding: '4px 0', borderBottom: '1px dashed var(--border)', fontSize: 12 }}>
          <div>
            <b>{t(`coam_firm_field_${c.field}`)}</b>{': '}
            {c.action === 'confirm' ? (
              // The value that was REVIEWED (the customer's cell when the review was made). It is the
              // customer's value now too while the review stands; once the cell has changed, the review
              // was not of the new value, so the new value is never shown as "kept" (data-stale-why
              // below says what the cell is now).
              <span data-firm-kept>{t('coam_firm_kept')} <span data-reviewed-value><CellValue field={c.field} v={c.sourceValue} /></span></span>
            ) : (
              <span>
                <span data-customer-value style={{ textDecoration: c.stale ? 'none' : 'line-through', color: 'var(--text-secondary)' }}><CellValue field={c.field} v={c.customerValue} /></span>
                {' → '}
                <span data-firm-value style={{ fontWeight: 700 }}><CellValue field={c.field} v={c.firmValue} /></span>
              </span>
            )}
            {c.stale && (
              <span data-stale-badge title={c.staleWhy} style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '0 6px', borderRadius: 8, background: amber.bg, color: amber.fg, border: `1px solid ${amber.border}` }}>
                {t('coam_firm_stale')}
              </span>
            )}
          </div>
          {c.stale && <div data-stale-why lang="en" style={{ color: amber.fg }}>{c.staleWhy}</div>}
          <div data-firm-reason style={{ color: 'var(--text-primary)' }}>{c.reason}</div>
          <div data-firm-meta style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>
            {t('coam_firm_by', { who: c.author })} · {fmtDateTime(c.at)} · {t('coam_firm_recorded', { who: c.recordedByName, src: c.recordedBy })}
            {c.decisionRef && <> · <span data-firm-ref>{c.decisionRef}</span></>}
          </div>
        </div>
      ))}
      <button data-firm-rule={row.code} onClick={() => onRule(row)} title={t('coam_firm_rule_tip', { code: row.code })}
        style={{ border: 'none', background: 'none', padding: '4px 0', color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
        <i className="ti ti-gavel" aria-hidden /> {t('coam_firm_rule')}
      </button>
    </div>
  );
}

type Problems = string[];

/** Record one judgement on one of a row's cells. */
export function FirmDialog({ templateId, row, firm, onClose, onSaved }: {
  templateId: string; row: CoaAccount; firm: CoaFirmSummary;
  onClose: () => void; onSaved: (firm: CoaFirmSummary) => void;
}) {
  const { t } = useTranslation();
  const judged = Object.keys(row.firm?.cells || {}) as CoaFirmField[];
  const [f, setF] = useState<CoaFirmField>(judged[0] || 'account_type');
  const customerOf = (x: CoaFirmField): string => (x === 'control_role' ? '' : String((row.customer || row)[x] ?? ''));
  const current = row.firm?.cells?.[f];
  const [action, setAction] = useState<'set' | 'confirm' | 'withdraw'>('set');
  const initialValue = (x: CoaFirmField) => {
    const c = row.firm?.cells?.[x];
    return c && c.action === 'set' ? c.firmValue : x === 'control_role' ? '' : customerOf(x);
  };
  const [value, setValue] = useState(initialValue(f));
  const [reason, setReason] = useState('');
  const [ref, setRef] = useState('');
  const [problems, setProblems] = useState<Problems>([]);
  const [saving, setSaving] = useState(false);
  const history = useMemo(() => firm.history.filter(h => h.code === row.code && h.field === f), [firm.history, row.code, f]);
  const dirty = reason.trim() !== '' || ref.trim() !== '' || (action === 'set' && value !== initialValue(f));

  const pickField = (x: CoaFirmField) => { setF(x); setValue(initialValue(x)); setProblems([]); if (action === 'withdraw' && !row.firm?.cells?.[x]) setAction('set'); };
  const close = () => {
    if (saving) return;
    if (dirty && !window.confirm(t('coam_firm_discard'))) return;
    onClose();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const submit = async () => {
    if (saving) return;
    if (!reason.trim()) { setProblems([t('coam_firm_reason_required')]); return; }
    setSaving(true); setProblems([]);
    try {
      const r = await coaMasterApi.addOverride(templateId, {
        code: row.code, field: f, action, sourceValue: customerOf(f),
        ...(action === 'set' ? { firmValue: value } : {}), reason, decisionRef: ref,
      });
      onSaved(r.firm);
    } catch (e: any) {
      const list = e?.payload?.problems;
      setProblems(Array.isArray(list) && list.length ? list.map(String) : [String(e?.message || e)]);
    } finally {
      setSaving(false);
    }
  };

  const valueInput = f === 'account_type' ? (
    <select aria-label={t('coam_firm_value')} value={value} onChange={e => setValue(e.target.value)} style={field}>
      {firm.words.map(w => <option key={w} value={w}>{w}</option>)}
    </select>
  ) : f === 'control_role' ? (
    <select aria-label={t('coam_firm_value')} value={value} onChange={e => setValue(e.target.value)} style={field}>
      <option value="">{t('coam_firm_none')}</option>
      {/* The roles the SERVER accepts (services/coaRules.ts CONTROL_ROLES, sent as firm.controlRoles). */}
      {firm.controlRoles.map(r => <option key={r} value={r}>{r === 'ar' ? t('coam_firm_role_ar') : r === 'ap' ? t('coam_firm_role_ap') : r} ({r})</option>)}
    </select>
  ) : (
    <input aria-label={t('coam_firm_value')} value={value} onChange={e => setValue(e.target.value)} style={{ ...field, width: '100%' }} />
  );
  const radio = (a: 'set' | 'confirm' | 'withdraw', label: string, disabled = false) => (
    <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: disabled ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
      <input type="radio" name="coam-firm-action" value={a} checked={action === a} disabled={disabled} onChange={() => { setAction(a); setProblems([]); }} />
      {label}
    </label>
  );
  const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 3 };

  return (
    <div data-dialog="coa-firm" onClick={close}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15, 23, 42, 0.28)' }}>
      <div role="dialog" aria-modal="true" aria-label={t('coam_firm_dialog_title', { code: row.code })} onClick={e => e.stopPropagation()}
        style={{ width: 'min(560px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 12, padding: 18, boxShadow: '0 12px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{t('coam_firm_dialog_title', { code: row.code })}</div>
        <div style={{ ...muted, marginBottom: 12 }}><span lang="th">{(row.customer || row).name_th}</span> · <span lang="en">{(row.customer || row).name_en}</span></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={lbl}>{t('coam_firm_field')}</div>
            <select aria-label={t('coam_firm_field')} value={f} onChange={e => pickField(e.target.value as CoaFirmField)} style={{ ...field, width: '100%' }}>
              {firm.fields.map(x => <option key={x} value={x}>{t(`coam_firm_field_${x}`)}</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>{t('coam_firm_customer_value')}</div>
            <div data-dialog-customer style={{ fontSize: 13, padding: '6px 0' }}><CellValue field={f} v={customerOf(f)} /></div>
          </div>
        </div>
        {current && (
          <div data-dialog-current style={{ ...muted, marginBottom: 10 }}>
            {/* A judgement that no longer stands is not called "in force": it is named for what it is. */}
            {current.stale ? <span style={{ color: amber.fg }}>{t('coam_firm_stale')} ·</span> : t('coam_firm_in_force')}{' '}
            {current.action === 'confirm'
              ? <>{t('coam_firm_kept')} <b><CellValue field={f} v={current.sourceValue} /></b></>
              : <b><CellValue field={f} v={current.firmValue} /></b>}
            {' — '}{current.reason} ({current.author}, {fmtDateTime(current.at)})
          </div>
        )}
        <div style={lbl}>{t('coam_firm_action')}</div>
        <div role="radiogroup" aria-label={t('coam_firm_action')} style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
          {radio('set', t('coam_firm_act_set'))}
          {radio('confirm', t('coam_firm_act_confirm'))}
          {radio('withdraw', t('coam_firm_act_withdraw'), !current)}
        </div>
        {action === 'set' && (
          <div style={{ marginBottom: 12 }}>
            <div style={lbl}>{t('coam_firm_value')}</div>
            {valueInput}
            {f === 'account_type' && <div style={{ ...muted, fontSize: 11.5, marginTop: 3 }}>{t('coam_firm_words_hint')}</div>}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>{t('coam_firm_reason')} <span style={{ color: '#B42318' }}>*</span></div>
          <textarea aria-label={t('coam_firm_reason')} aria-required="true" value={reason} onChange={e => setReason(e.target.value)} rows={3}
            style={{ ...field, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>{t('coam_firm_decision_ref')}</div>
          <input aria-label={t('coam_firm_decision_ref')} value={ref} onChange={e => setRef(e.target.value)} style={{ ...field, width: '100%' }} />
        </div>
        {problems.length > 0 && (
          <div role="alert" data-dialog-error style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>
            {problems.map((p, i) => <div key={i} data-dialog-problem>{p}</div>)}
          </div>
        )}
        {history.length > 0 && (
          <div data-dialog-history style={{ marginBottom: 12 }}>
            <div style={lbl}>{t('coam_firm_history')}</div>
            {history.map(h => (
              <div key={h.id} data-history-id={h.id} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '2px 0' }}>
                {fmtDateTime(h.at)} · {t(`coam_firm_act_${h.action}`, { defaultValue: h.action })} · <CellValue field={f} v={h.firmValue} /> · {h.author}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={close} disabled={saving} style={{ ...field, cursor: 'pointer' }}>{t('coam_firm_cancel')}</button>
          <button data-dialog-submit onClick={submit} disabled={saving}
            style={{ ...field, cursor: saving ? 'default' : 'pointer', background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)', fontWeight: 700 }}>
            <i className={`ti ${saving ? 'ti-loader-2' : 'ti-check'}`} style={{ fontSize: 13, animation: saving ? 'spin 0.9s linear infinite' : undefined }} aria-hidden /> {t('coam_firm_submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
